import { Table } from "dexie";
import { dexieDB } from "@/services/dexie-database";
import { LogService } from "@/services/logging";

/**
 * Atomically replaces all data in a Dexie table within a single transaction.
 * If either clear() or bulkPut() fails, the entire operation is rolled back
 * and the original data remains intact.
 *
 * Best for small tables where accumulating all data in memory is acceptable
 * (e.g., promocode campaigns, pictures).
 *
 * @param table - The Dexie table to replace data in
 * @param newData - The complete set of new records to insert
 */
export async function atomicTableSwap(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any, any>,
  newData: unknown[]
): Promise<void> {
  await dexieDB.transaction("rw", table, async () => {
    await table.clear();
    if (newData.length > 0) {
      await table.bulkPut(newData);
    }
  });
  LogService.info(
    `atomicTableSwap: Replaced ${table.name} with ${newData.length} records`
  );
}

/**
 * Removes records from a Dexie table whose primary key is NOT in the given set.
 *
 * Used in the "upsert-then-prune" pattern for large tables:
 *   1. Fetch pages from remote, upsert each page into Dexie (streaming, memory-friendly)
 *   2. Track all new record IDs
 *   3. After all fetches succeed, call this to remove stale records
 *
 * If the fetch fails mid-way, the table contains a mix of old + partial new data,
 * which is still usable — unlike the "clear-then-fetch" pattern where the table is empty.
 *
 * @param table - The Dexie table to prune
 * @param freshIds - Set of primary keys that should be kept
 */
export async function pruneStaleRecords(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any, any>,
  freshIds: Set<number>
): Promise<void> {
  const staleKeys: number[] = [];
  await table.each((record, cursor) => {
    if (!freshIds.has(cursor.primaryKey as number)) {
      staleKeys.push(cursor.primaryKey as number);
    }
  });
  if (staleKeys.length > 0) {
    await table.bulkDelete(staleKeys);
  }
  LogService.info(
    `pruneStaleRecords: Pruned ${staleKeys.length} stale records from ${table.name}, kept ${freshIds.size}`
  );
}
